import React, { useEffect, useRef, useState } from "react";
import { Field, ErrorMessage, useFormikContext } from "formik";
import { listSubProjects } from "services/entityProjectService";
import CustomSelect from "./CustomSelect";

const idsOf = (value) => Array.isArray(value) ? value : [];
const projectIdsOf = (value) => [...new Set(String(value || "").split("||").map((id) => id.trim()).filter(Boolean))];
const sameIds = (first, second) => first.length === second.length && first.every((id) => second.includes(id));

function SubProjectPicker({ entityId, projectKey, projectNames, isEdit, initialSelection }) {
  const { values, setFieldValue, validateField } = useFormikContext();
  const [lookups, setLookups] = useState({});
  const [attempt, setAttempt] = useState(0);
  const [searches, setSearches] = useState({});
  const cache = useRef(new Map());
  const previousKey = useRef(projectKey);
  const pruneSelection = useRef(false);
  const projectIds = projectIdsOf(projectKey);
  const selected = idsOf(values.subProjectIds);
  const unchanged = isEdit && sameIds(projectIdsOf(initialSelection.projectId), projectIds) && sameIds(initialSelection.subProjectIds, selected);

  useEffect(() => {
    const controllers = [];
    projectIdsOf(projectKey).forEach((projectId) => {
      if (cache.current.has(projectId)) return;
      const controller = new AbortController();
      controllers.push(controller);
      setLookups((current) => ({ ...current, [projectId]: { loading: true, rows: [], error: "" } }));
      listSubProjects(entityId, projectId, { signal: controller.signal, includeArchived: isEdit }).then((rows) => {
        if (controller.signal.aborted) return;
        cache.current.set(projectId, rows);
        setLookups((current) => ({ ...current, [projectId]: { loading: false, rows, error: "" } }));
      }).catch((requestError) => {
        if (!controller.signal.aborted) setLookups((current) => ({ ...current, [projectId]: { loading: false, rows: [], error: requestError.response?.data?.message || "Unable to load sub-projects." } }));
      });
    });
    return () => controllers.forEach((controller) => controller.abort());
  }, [entityId, projectKey, isEdit, attempt]);

  useEffect(() => {
    if (previousKey.current !== projectKey) {
      previousKey.current = projectKey;
      pruneSelection.current = true;
    }
    const currentParents = projectIdsOf(projectKey);
    if (!pruneSelection.current || currentParents.some((id) => !lookups[id] || lookups[id].loading || lookups[id].error)) return;
    const allowedIds = new Set(currentParents.flatMap((id) => lookups[id].rows.map((row) => row._id)));
    const currentIds = idsOf(values.subProjectIds);
    const retained = currentIds.filter((id) => allowedIds.has(id));
    pruneSelection.current = false;
    if (!sameIds(currentIds, retained)) setFieldValue("subProjectIds", retained);
  }, [projectKey, lookups, values.subProjectIds, setFieldValue]);

  useEffect(() => { validateField("subProjectIds"); }, [projectKey, lookups, values.subProjectIds, validateField]);

  const validate = (value) => {
    const ids = idsOf(value);
    if (isEdit && sameIds(projectIdsOf(initialSelection.projectId), projectIds) && sameIds(initialSelection.subProjectIds, ids)) return undefined;
    const allowedIds = new Set();
    for (const projectId of projectIds) {
      const group = lookups[projectId];
      if (!group || group.loading) return "Please wait for the sub-projects to load.";
      if (group.error) return "Load the sub-projects before saving this RFQ.";
      const picked = ids.filter((id) => group.rows.some((row) => row._id === id));
      if (group.rows.some((row) => !row.isDeleted) && !picked.length) return projectIds.length > 1 ? `Select at least one sub-project for "${projectNames[projectId] || projectId}".` : "Select at least one sub-project.";
      group.rows.forEach((row) => {
        if (!row.isDeleted || (isEdit && projectIdsOf(initialSelection.projectId).includes(projectId) && initialSelection.subProjectIds.includes(row._id))) allowedIds.add(row._id);
      });
    }
    if (ids.some((id) => !allowedIds.has(id))) return "Select active sub-projects from the selected main projects only.";
    return undefined;
  };

  return <Field name="subProjectIds" validate={validate}>{() => <div className="mt-3 space-y-3 empty:hidden">
    {projectIds.map((projectId) => {
      const { rows = [], loading = true, error = "" } = lookups[projectId] || {};
      const selectable = rows.filter((row) => !row.isDeleted || selected.includes(row._id));
      if (!loading && !error && !selectable.length) return null;
      const search = searches[projectId] || "";
      const visible = selectable.filter((row) => row.name.toLowerCase().includes(search.trim().toLowerCase()));
      const picked = selected.filter((id) => rows.some((row) => row._id === id));
      return <div key={projectId} role="group" aria-label={`Sub-projects for ${projectNames[projectId] || projectId}`} className="rounded-lg border border-orange-100 bg-orange-50/40 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2"><p className="text-xs font-semibold text-gray-800">Sub-projects{projectIds.length > 1 ? ` - ${projectNames[projectId] || projectId}` : ""}{rows.some((row) => !row.isDeleted) && !unchanged ? " *" : ""}</p>{picked.length > 0 && <span className="text-xs text-orange-700">{picked.length} selected</span>}</div>
        {loading ? <p role="status" className="mt-2 text-xs text-gray-500">Loading sub-projects...</p> : error ? <p role="alert" className="mt-2 text-xs text-red-600">{error} <button type="button" onClick={() => setAttempt((value) => value + 1)} className="underline">Retry</button></p> : <>
          {rows.length > 6 && <input type="search" aria-label={`Search sub-projects for ${projectNames[projectId] || projectId}`} value={search} onChange={(event) => setSearches((current) => ({ ...current, [projectId]: event.target.value }))} placeholder="Search sub-projects" className="mt-2 w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs focus:ring-orange-100" />}
          <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">{visible.map((row) => <label key={row._id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-xs hover:bg-white">
            <input type="checkbox" checked={selected.includes(row._id)} onChange={(event) => setFieldValue("subProjectIds", event.target.checked ? [...selected, row._id] : selected.filter((id) => id !== row._id))} className="rounded text-orange-500 focus:ring-orange-200" />
            <span className="min-w-0"><span className="font-medium">{row.name}</span><span className="ml-2 text-gray-500">{row.projectType}{row.isDeleted ? " (archived; saved RFQ selection)" : ""}</span></span>
          </label>)}</div>
          {!visible.length && <p className="mt-2 text-xs text-gray-500">No matching sub-projects.</p>}
        </>}
        {unchanged && !selected.length && rows.some((row) => !row.isDeleted) && <p className="mt-2 text-xs text-gray-500">This existing RFQ can keep its main-project link, or you can select sub-projects.</p>}
      </div>;
    })}
    <ErrorMessage name="subProjectIds" component="p" className="mt-2 text-xs text-red-600" />
  </div>}</Field>;
}

export default function RfqProjectSelector({ projects, entityId, isEdit, onAddNew, multipleSelection = false }) {
  const { values, setValues, validateField } = useFormikContext();
  const initialSelection = useRef({ projectId: values.projectName, subProjectIds: idsOf(values.subProjectIds) });
  const projectIds = projectIdsOf(values.projectName);
  const savedProjectIds = new Set(isEdit ? projectIdsOf(initialSelection.current.projectId) : []);
  const selectableProjects = projects.filter((project) => project.isActive !== false || savedProjectIds.has(project._id));
  const projectNames = Object.fromEntries(projects.map((project) => [project._id, project.name]));
  useEffect(() => { validateField("projectName"); }, [projects, validateField]);
  const validateMainProject = (value) => {
    const selectedIds = String(value || "").split("||").map((id) => id.trim());
    if (projects.some((project) => project.isActive === false && selectedIds.includes(project._id) && !savedProjectIds.has(project._id))) {
      return "This main project is inactive. Select an active project.";
    }
    return undefined;
  };
  return <>
    <label htmlFor="projectName" className="mb-1 block text-sm font-medium">Main project<span className="ml-0.5 text-red-500">*</span></label>
    <Field component={CustomSelect} id="projectName" name="projectName" validate={validateMainProject} options={selectableProjects.map((project) => ({ value: project._id, label: project.isActive === false ? `${project.name} (Inactive)` : project.name, description: [project.location?.city, project.location?.state].filter(Boolean).join(", ") }))} selectedId={values.projectName} placeholder="Select main project" multipleSelection={multipleSelection} onAddNew={onAddNew} onChange={(option) => setValues((current) => ({ ...current, projectName: option.value, subProjectIds: option.value ? idsOf(current.subProjectIds) : [] }))} />
    <ErrorMessage name="projectName" component="p" className="mt-1 text-xs text-red-600" />
    {projectIds.length > 0 && <SubProjectPicker key={`${entityId}:${Boolean(isEdit)}`} entityId={entityId} projectKey={projectIds.join("||")} projectNames={projectNames} isEdit={isEdit} initialSelection={initialSelection.current} />}
  </>;
}
